import { FlattenedNode, TreeNode } from './types'

/**
 * Flatten a tree structure into a list of nodes with depth information.
 * Only includes children of expanded nodes.
 */
export function flattenTree(
  nodes: TreeNode[],
  expandedIds: Set<string>,
  depth = 0,
  parentId: string | null = null
): FlattenedNode[] {
  const result: FlattenedNode[] = []

  for (const node of nodes) {
    const hasNodeChildren = node.children && node.children.length > 0
    const isExpanded = hasNodeChildren && expandedIds.has(node.id)

    result.push({
      data: node,
      depth,
      id: node.id,
      index: result.length,
      isExpanded: !!isExpanded,
      parentId
    })

    if (isExpanded && node.children) {
      result.push(
        ...flattenTree(node.children, expandedIds, depth + 1, node.id)
      )
    }
  }

  return result
}

/**
 * Rebuild a tree from a flattened list using parentId relationships.
 * Preserves collapsed children from the original node data.
 */
export function buildTree(flattenedItems: FlattenedNode[]): TreeNode[] {
  const root: { children: TreeNode[]; id: string } = {
    children: [],
    id: 'root'
  }
  const nodeMap: Record<string, TreeNode> = { root }

  const items = flattenedItems.map((item) => ({
    flatItem: item,
    node: { ...item.data, children: [] as TreeNode[] }
  }))

  for (const { flatItem, node } of items) {
    const parentId = flatItem.parentId ?? 'root'
    const parent =
      nodeMap[parentId] ?? items.find((i) => i.node.id === parentId)?.node

    if (!parent) continue

    nodeMap[node.id] = node
    parent.children?.push(node)
  }

  // Restore collapsed children (in original data but not in the flat list)
  const flatIds = new Set(flattenedItems.map((i) => i.id))

  for (const { flatItem, node } of items) {
    const originalChildren = flatItem.data.children || []

    for (const origChild of originalChildren) {
      if (!flatIds.has(origChild.id)) {
        node.children.push(origChild)
      }
    }
  }

  return root.children
}

/**
 * Check if a node has children in the tree data.
 */
export function hasChildren(nodeId: string, data: TreeNode[]): boolean {
  for (const node of data) {
    if (node.id === nodeId) {
      return !!(node.children && node.children.length > 0)
    }

    if (node.children) {
      const found = hasChildren(nodeId, node.children)

      if (found) return true
    }
  }

  return false
}

/**
 * Check if any node in the tree has children.
 */
export function anyNodeHasChildren(data: TreeNode[]): boolean {
  return data.some((node) => node.children && node.children.length > 0)
}

/**
 * Count all descendants of a node in the tree data (regardless of expansion).
 */
export function countAllDescendants(
  nodeId: string,
  data: TreeNode[]
): number {
  const countChildren = (nodes: TreeNode[]): number =>
    nodes.reduce(
      (sum, node) => sum + 1 + countChildren(node.children || []),
      0
    )

  const findNode = (nodes: TreeNode[]): TreeNode | null => {
    for (const node of nodes) {
      if (node.id === nodeId) return node

      if (node.children) {
        const found = findNode(node.children)

        if (found) return found
      }
    }

    return null
  }

  const node = findNode(data)

  return node ? countChildren(node.children || []) : 0
}

/**
 * Get all descendant IDs from a flattened list by following parentId chains.
 */
export function getDescendants(
  items: FlattenedNode[],
  parentId: string
): Set<string> {
  const directChildren = items.filter((item) => item.parentId === parentId)

  return directChildren.reduce((descendants, child) => {
    return new Set([
      ...descendants,
      child.id,
      ...getDescendants(items, child.id)
    ])
  }, new Set<string>())
}

/**
 * Compute the drag depth offset from horizontal pointer movement.
 */
export function getDragDepth(offset: number, indentationWidth: number) {
  return Math.round(offset / indentationWidth)
}

/**
 * Project the valid depth and parentId for a dragged item based on its
 * neighbors in the flat list and the desired depth.
 */
export function getProjection(
  items: FlattenedNode[],
  targetId: string,
  projectedDepth: number
) {
  const targetItemIndex = items.findIndex(({ id }) => id === targetId)
  const nextItem = items[targetItemIndex + 1]
  const previousItem = items[targetItemIndex - 1]
  const targetItem = items[targetItemIndex]
  const maxDepth = getMaxDepth(previousItem, targetItem)
  const minDepth = getMinDepth(nextItem)
  let depth = projectedDepth

  if (projectedDepth >= maxDepth) {
    depth = maxDepth
  } else if (projectedDepth < minDepth) {
    depth = minDepth
  }

  return { depth, parentId: getParentId() }

  function getParentId(): string | null {
    if (depth === 0 || !previousItem) {
      return null
    }

    if (depth === previousItem.depth) {
      return previousItem.parentId
    }

    if (depth > previousItem.depth) {
      return previousItem.id as string
    }

    const newParent = items
      .slice(0, targetItemIndex)
      .reverse()
      .find((item) => item.depth === depth)?.parentId

    return newParent ?? null
  }
}

function getMaxDepth(
  previousItem: FlattenedNode | undefined,
  targetItem: FlattenedNode | undefined
) {
  if (!previousItem) return 0

  return Math.min((targetItem?.depth ?? 0) + 1, previousItem.depth + 1)
}

function getMinDepth(nextItem: FlattenedNode | undefined) {
  return nextItem ? nextItem.depth : 0
}
